import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { GameProvider, useGame } from './contexts/GameContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import HomePage from './pages/HomePage';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Profile from './components/auth/Profile';
import DashboardPage from './pages/DashboardPage';
import GamePage from './pages/GamePage';
import GameResultsPage from './pages/GameResultsPage';
import MultiplayerPage from './pages/MultiplayerPage';
import MultiplayerRoomPage from './pages/MultiplayerRoomPage';
import Leaderboard from './components/leaderboard/Leaderboard';
import AdminDashboard from './pages/admin/AdminDashboard';
import LocationManagement from './pages/admin/LocationManagement';
import AddLocation from './pages/admin/AddLocation';
import LevelManagement from './pages/admin/LevelManagement';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="page-loading"><div className="loader" /></div>;
  return user ? children : <Navigate to="/login" state={{ from: location }} replace />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-loading"><div className="loader" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'admin') {
    alert('Access denied. Admin privileges required.');
    return <Navigate to="/dashboard" />;
  }
  return children;
};

const Layout = ({ children }) => {
  const { isPlaying } = useGame();
  const hideNav = isPlaying;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {!hideNav && <Header />}
      <main style={{ flex: 1, paddingTop: hideNav ? 0 : 'var(--header-h)' }}>
        {children}
      </main>
      {!hideNav && <Footer />}
    </div>
  );
};

const AppRoutes = () => (
  <Layout>
    <Routes>
      <Route path="/"            element={<HomePage />} />
      <Route path="/login"       element={<Login />} />
      <Route path="/register"    element={<Register />} />
      <Route path="/leaderboard" element={<Leaderboard />} />

      <Route path="/game"         element={<PrivateRoute><GamePage /></PrivateRoute>} />
      <Route path="/game/results" element={<PrivateRoute><GameResultsPage /></PrivateRoute>} />
      <Route path="/dashboard"   element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/profile"     element={<PrivateRoute><Profile /></PrivateRoute>} />

      <Route path="/multiplayer"           element={<PrivateRoute><MultiplayerPage /></PrivateRoute>} />
      <Route path="/multiplayer/:roomCode" element={<PrivateRoute><MultiplayerRoomPage /></PrivateRoute>} />

      <Route path="/admin/dashboard"       element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/locations"       element={<AdminRoute><LocationManagement /></AdminRoute>} />
      <Route path="/admin/locations/new"   element={<AdminRoute><AddLocation /></AdminRoute>} />
      <Route path="/admin/levels"          element={<AdminRoute><LevelManagement /></AdminRoute>} />
    </Routes>
  </Layout>
);

const App = () => (
  <ThemeProvider>
    <AuthProvider>
      <GameProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </GameProvider>
    </AuthProvider>
  </ThemeProvider>
);

export default App;
